import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { PageHeader } from '../components/layout/PageHeader';
import { CtaBand } from '../components/ui/CtaBand';
import { articles, articleCategories } from '../data/library';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useBrand } from '../contexts/BrandContext';
export function Library() {
  const [category, setCategory] = useState<string>('All');
  const {
    brand
  } = useBrand();
  useDocumentMeta({
    title: `The unanswered questions — ${brand.name}`,
    description: 'Clinician-reviewed answers to the questions women actually ask about perimenopause and menopause. Free, unpaywalled, dated, and honest about what the evidence does not settle.'
  });
  const filtered = category === 'All' ? articles : articles.filter(article => article.category === category);
  return <main className="w-full bg-cream" data-id="element-607" data-mp="3gzmurwq0jka3">
      <PageHeader eyebrow="The unanswered questions" title="The things you typed at 3am." intro="Every piece is titled as the question you actually asked. Every one is reviewed by a named clinician, dated, and ends with what the evidence genuinely does not settle. None of it is behind an email address, and none of it ever will be." data-id="element-608" data-mp="3gwh7mn0y2sh0" />

      <section className="bg-cream" data-id="element-609" data-mp="30njjh0x57y4k">
        <div className="mx-auto max-w-shell px-6 pb-20 pt-10 md:px-8 md:pb-28 md:pt-14" data-id="element-610" data-mp="30okvp8pwaqnv">
          <div className="flex flex-wrap gap-2.5" role="group" aria-label="Filter the library" data-id="element-611" data-mp="197fjuh0g67g">
            {articleCategories.map(item => <button key={item} type="button" onClick={() => setCategory(item)} aria-pressed={category === item} className={`rounded-brand border px-5 py-2.5 text-sm transition-colors duration-150 ease-brand ${category === item ? 'border-brick bg-brick text-cream' : 'border-ink/20 text-ink-soft hover:border-ink/45 hover:text-ink'}`} data-id="element-612" data-mp="3ojdu5rzqsky4">
                {item}
              </button>)}
          </div>

          {filtered.length === 0 ? <p className="rule mt-12 pt-12 text-ink-soft" data-id="element-613" data-mp="q7j0rjvfsuhp">
              We have not written that one yet. If it is the question keeping you up, tell your
              clinician and we will add it — that is genuinely how this library gets written.
            </p> : <ul className="mt-12" data-id="element-614" data-mp="l9hekq2psf20">
              {filtered.map(article => <li key={article.slug} className="rule" data-id="element-615" data-mp="79pymjsn0old">
                  <Link to={`/library/${article.slug}`} className="group grid gap-4 py-9 md:grid-cols-[1.1fr_1.2fr_auto] md:items-start md:gap-12" data-id="element-616" data-mp="7b9be0f34hie">
                    <div data-id="element-617" data-mp="21lcxqiikbb7k">
                      <p className="text-[0.6875rem] uppercase tracking-label text-brick" data-id="element-618" data-mp="25ost7biwb5io">
                        {article.category}
                      </p>
                      <h2 className="mt-3 font-serif text-[1.75rem] leading-snug text-ink transition-colors duration-150 ease-brand group-hover:text-brick md:text-[2rem]" data-id="element-619" data-mp="3q7d3iv1698rn">
                        {article.question}
                      </h2>
                    </div>
                    <div data-id="element-620" data-mp="3v557inf3clc7">
                      <p className="leading-relaxed text-ink-soft" data-id="element-621" data-mp="3v4vfyveee64e">{article.standfirst}</p>
                      <p className="mt-4 text-sm text-ink-muted" data-id="element-622" data-mp="do55tm761avk">
                        {article.readMinutes} min read · Reviewed by {article.reviewedBy} ·{' '}
                        {article.reviewedOn}
                      </p>
                    </div>
                    <ArrowRightIcon className="hidden h-5 w-5 shrink-0 text-brick transition-transform duration-150 ease-brand group-hover:translate-x-1 md:mt-2 md:block" aria-hidden="true" data-id="element-623" data-mp="hqs4o7297w29" />
                  </Link>
                </li>)}
            </ul>}
        </div>
      </section>

      <CtaBand title="Reading this at 3am?" intro="The thread is open and moderated overnight. Or if you would rather have a clinician look at this properly, start with the one thing bothering you most — it takes about four minutes." data-id="element-624" data-mp="2lzo28z232omb" />
    </main>;
}